import type { AuthenticationRequiredAxiosResponse } from '@shared/common/network/data/model/authentication-required-axios-response.model';
import type { InternalAxiosRequestConfigWithRetry } from '@shared/common/network/data/model/internal-axios-request-config-with-retry.model';
import { isAxiosError } from 'axios';
import { BlockedRequestError, NetworkError } from '@kiplin/web-common/network';

export const axiosResponseErrorInterceptor = <E = any>(error: E): void => {
  // 1. Check if error is an axios error.
  if (!isAxiosError(error)) {
    throw error;
  }

  // 2. Check if error is a kind of network error (e.g., CORS, offline, code 0)
  if (!error.response) {
    // Catch a untyped Network Error, type it as a NetworkError to catch it later easily.
    if (error.message.includes('Network Error')) {
      throw new NetworkError(error, error.config?.url || '');
    }
    const blockedRequestError = new BlockedRequestError(error);
    // eslint-disable-next-line no-console
    console.error(blockedRequestError);
    throw blockedRequestError;
  }

  const httpStatus = error.response.status;
  if (httpStatus !== 403) {
    throw error;
  }

  // 3. Check if error has a config property for the next operation
  if (error.config === undefined) {
    throw error;
  }

  const originalRequest = error.config as InternalAxiosRequestConfigWithRetry;
  originalRequest.attemptNumber = (originalRequest.attemptNumber ?? -1) + 1;

  if (originalRequest.attemptNumber >= 1) {
    // In case when token doesn't refresh, send response with new property
    // eslint-disable-next-line no-param-reassign
    (error.response as AuthenticationRequiredAxiosResponse).isAuthenticationRequired = true;
    throw error;
  }
};
